import { createFileRoute, Link } from "@tanstack/react-router";

import { LegalPage, LegalSection } from "../components/legal-page";
import { seo } from "../lib/head";
import { LEGAL } from "../lib/legal";

export const Route = createFileRoute("/confidentialite")({
  staticData: { ssr: true },
  head: () =>
    seo({
      title: "Politique de confidentialité · Listou",
      description:
        "Les données que Listou collecte, pourquoi, combien de temps elles sont gardées et comment exercer vos droits.",
    }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const mail = <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a>;
  return (
    <LegalPage title="Politique de confidentialité">
      <LegalSection title="Qui est responsable">
        <p>
          Le responsable du traitement est {LEGAL.publisher.name} (voir les{" "}
          <Link to="/mentions-legales">mentions légales</Link>). Pour toute question sur vos données
          : {mail}.
        </p>
      </LegalSection>

      <LegalSection title="Ce que nous collectons, et pourquoi">
        <ul>
          <li>
            <strong>Votre compte</strong> : adresse email et nom, pour vous connecter et vous
            envoyer les emails liés à vos listes. Si vous passez par Google, nous recevons aussi
            l'identifiant de votre compte Google. Base légale : l'exécution du service que vous
            demandez.
          </li>
          <li>
            <strong>Un compte sans adresse</strong> : en choisissant votre prénom dans un groupe
            sans vous inscrire, un compte est créé avec ce seul prénom, sans email. Il vous garde
            connecté sur cet appareil. Si vous cliquez sur « Ce n'est pas moi » et qu'il ne contient
            rien, il est supprimé aussitôt.
          </li>
          <li>
            <strong>Vos listes</strong> : titres, descriptions, date de l'événement, prénom de la
            personne fêtée, souhaits, liens, notes et photos que vous ajoutez. Base légale :
            l'exécution du service.
          </li>
          <li>
            <strong>Les réservations des invités</strong> : le prénom saisi (facultatif, transmis
            aux organisateurs de la liste, sauf option « Rester anonyme ») et l'email (facultatif,
            jamais affiché), qui sert uniquement à envoyer la confirmation et le lien d'annulation.
            Aucun compte n'est nécessaire.
          </li>
          <li>
            <strong>Les groupes</strong> : le nom du groupe, le prénom de son organisateur, les
            prénoms (ou noms de listes) de ses places, et qui a choisi quelle place. Toute personne
            qui a le lien du groupe voit ces prénoms et les premières idées de chaque liste
            partagée, jamais ce qui a été réservé.
          </li>
          <li>
            <strong>Les Secret Santa</strong> : le tirage (qui offre à qui), le budget, la date, le
            mot de l'organisateur, les gommettes de couleur et les exclusions. L'organisateur voit
            le tirage ; chaque participant en est prévenu. Un participant ne voit que le sien.
          </li>
          <li>
            <strong>Les adresses saisies par un organisateur</strong> : l'organisateur d'un Secret
            Santa peut indiquer l'email d'un participant pour lui envoyer son tirage. Elle ne sert
            qu'à ce groupe (le tirage, les rappels avant l'échange, le lien renvoyé à sa demande),
            n'est jamais montrée aux autres participants, et n'est utilisée que si l'organisateur a
            lui-même confirmé son adresse. Base légale : notre intérêt légitime et celui de
            l'organisateur à faire fonctionner le tirage qu'il organise. Chaque email contient un
            lien pour ne plus en recevoir.
          </li>
          <li>
            <strong>Les avis produits</strong> : note, commentaire et prénom de l'auteur, publiés
            sur la page du produit.
          </li>
          <li>
            <strong>L'origine de votre inscription</strong>, pour nos statistiques.
          </li>
          <li>
            <strong>La sécurité</strong> : adresse IP et navigateur associés à chaque session, pour
            vous permettre de voir et fermer vos sessions et pour limiter les abus. Base légale :
            notre intérêt légitime à protéger les comptes.
          </li>
        </ul>
        <p>
          Les clics vers les boutiques sont comptés sans IP ni identifiant : produit, boutique et
          localisation approximative (pays, région et ville déduits de l'adresse IP par notre
          hébergeur, l'adresse elle-même n'étant pas conservée). Ils servent à mesurer les
          commissions d'affiliation et à établir des statistiques par zone. Base légale : notre
          intérêt légitime.
        </p>
        <p>Nous ne vendons aucune donnée et n'affichons aucune publicité ciblée.</p>
      </LegalSection>

      <LegalSection title="Cookies et stockage local">
        <p>
          Listou n'utilise que des cookies indispensables au fonctionnement du site ou à sa mesure
          d'audience, qui ne demandent pas de consentement :
        </p>
        <ul>
          <li>un cookie de session, qui vous garde connecté (30 jours au plus) ;</li>
          <li>un cookie de sécurité contre les requêtes frauduleuses (CSRF) ;</li>
          <li>
            des cookies temporaires pendant une connexion avec Google, supprimés dès le retour sur
            le site.
          </li>
          <li>
            un cookie de mesure d'audience, qui retient d'où vient un visiteur jusqu'à son
            inscription (30 jours au plus).
          </li>
        </ul>
        <p>
          Quand vous réservez un cadeau sans compte, votre navigateur garde localement le moyen
          d'annuler cette réservation. Quand vous choisissez de « juste regarder » un groupe, il
          s'en souvient aussi, pour ne pas vous redemander votre prénom. Aucun traceur publicitaire
          n'est utilisé.
        </p>
      </LegalSection>

      <LegalSection title="Qui y a accès">
        <p>
          Vos données ne sont accessibles qu'à Listou et aux prestataires techniques ci-dessous,
          chacun pour sa seule mission :
        </p>
        <ul>
          <li>l'hébergeur du serveur, {LEGAL.host.name} ;</li>
          <li>PlanetScale, Inc., pour la base de données ;</li>
          <li>Amazon Web Services (Amazon SES et S3), pour l'envoi des emails et les images ;</li>
          <li>Cloudflare, Inc., par lequel transite le trafic du site ;</li>
          <li>
            OpenAI, pour l'assistant d'idées cadeaux (il reçoit le titre, la description et les
            souhaits de la liste concernée, sans votre email) et pour filtrer les avis abusifs ;
          </li>
          <li>Google, si vous choisissez de vous connecter avec votre compte Google.</li>
        </ul>
        <p>
          Certains de ces prestataires sont établis aux États-Unis. Les transferts s'appuient sur le
          cadre de protection des données UE–États-Unis (Data Privacy Framework) ou sur les clauses
          contractuelles types de la Commission européenne.
        </p>
      </LegalSection>

      <LegalSection title="Combien de temps">
        <ul>
          <li>Compte, listes et avis : tant que le compte existe.</li>
          <li>
            Réservations et email d'un invité : tant que la liste existe, ou jusqu'à l'annulation de
            la réservation.
          </li>
          <li>Sessions (IP et navigateur) : jusqu'à leur expiration, 30 jours au plus.</li>
          <li>Liens et codes de connexion : quelques minutes, puis supprimés sous 24 heures.</li>
          <li>
            Groupes, tirages et adresses saisies par un organisateur : tant que le groupe existe.
            Supprimer le groupe les efface ; les listes remplies par ses membres restent à eux.
          </li>
          <li>
            Désinscriptions : sans limite de durée, pour continuer à les respecter. Nous ne gardons
            pas l'adresse elle-même, seulement une empreinte chiffrée qui permet de la reconnaître.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Vos droits">
        <p>
          Vous pouvez à tout moment accéder à vos données, les rectifier, les exporter, les faire
          supprimer, ou vous opposer à un traitement. Écrivez à {mail} : nous répondons sous un
          mois. La suppression du compte efface vos avis et vos listes ; une liste que vous gérez
          avec un co-propriétaire lui est transmise plutôt que supprimée.
        </p>
        <p>
          Vous avez reçu un email de Secret Santa sans rien avoir demandé ? Le lien en bas de
          l'email vous désinscrit en un clic, sans compte : plus aucun email envoyé de la part d'un
          autre ne vous parviendra. Vous pouvez aussi demander à l'organisateur de retirer votre
          adresse, ou nous écrire.
        </p>
        <p>
          Si vous estimez que vos droits ne sont pas respectés, vous pouvez saisir la CNIL (
          <a href="https://www.cnil.fr/fr/plaintes" rel="noreferrer" target="_blank">
            cnil.fr/plaintes
          </a>
          ).
        </p>
      </LegalSection>
    </LegalPage>
  );
}
